import {
  isCreatedMessage,
  isFailUploadedMessage,
  isUploadedMessage,
  Message,
} from 'shared/domain/messages/message';
import type { FileServicesUnion } from 'shared/domain/messages/httpQueue/eventMessages';
import { areLocalIdsEqual, isLocalId } from 'helpers/idComparison';
import { isPlainObject } from 'shared/utils/object';
import { Progress, UploadProgresFile } from './types';

/** How an upload event of one file service changes the files it matches. */
export interface UploadProgressUpdate {
  matches: (file: UploadProgresFile) => boolean;
  changes: Partial<UploadProgresFile>;
}

function readEventLocalId(data: unknown): number | undefined {
  if (isLocalId(data)) {
    return data;
  }
  return isPlainObject(data) && isLocalId(data.localId) ? data.localId : undefined;
}

/** Whether the event (a local id, or `{ localId }`) is about the file. */
export function matchesLocalId(
  file: { localId?: number },
  event: { data?: unknown }
): boolean {
  const eventLocalId = readEventLocalId(event.data);
  return eventLocalId !== undefined && areLocalIdsEqual(file.localId, eventLocalId);
}

function readCreatedLocalId(event: Message): number | undefined {
  return Array.isArray(event.data) && isLocalId(event.data[0]) ? event.data[0] : undefined;
}

/**
 * The change an event of a file service (documents or documentation) makes to
 * the upload progress: the created file gets its local id (matched by its
 * `uniqueId`), then becomes uploaded or failed (matched by the local id).
 */
export function toUploadProgressUpdate(
  type: FileServicesUnion,
  event: Message
): UploadProgressUpdate | undefined {
  const isFileOfEvent = (file: UploadProgresFile): boolean =>
    file.type === type && matchesLocalId(file, event);

  if (isCreatedMessage(event)) {
    const localId = readCreatedLocalId(event);
    return {
      matches: (file) => file.uniqueFileId === event.uniqueId,
      changes: localId === undefined ? { status: Progress.PENDING } : { localId, status: Progress.PENDING },
    };
  }
  if (isUploadedMessage(event)) {
    return { matches: isFileOfEvent, changes: { status: Progress.UPLOADED } };
  }
  if (isFailUploadedMessage(event)) {
    return { matches: isFileOfEvent, changes: { status: Progress.FAILED } };
  }
  return undefined;
}
